import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatRadioModule } from '@angular/material/radio';
import { L10N_LOCALE, L10nTranslatePipe } from 'angular-l10n';
import { Subscription } from 'rxjs';
import { MetadataService, MetadataViewModel } from 'src/app/boxwood-api';
import { IncidentCategory, IncidentEvent, IncidentType } from 'src/app/hickory-api';
import { IncidentModelHelper } from 'src/app/shared/incident';
import { LoadingComponent } from 'src/app/shared/loading/loading.component';
import { SelectorRowComponent } from 'src/app/shared/selector-row/selector-row.component';

export interface IncidentSelectTypeData {
  pageTitle?: string;
  selectMultiple?: boolean;
  selected?: IncidentType[];
}

@Component({
  selector: 'app-incident-select-type',
  templateUrl: './select-type.component.html',
  styleUrls: ['./select-type.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatPaginatorModule,
    MatRadioModule,
    L10nTranslatePipe,
    LoadingComponent,
    SelectorRowComponent,
  ]
})
export class IncidentSelectTypeComponent implements OnInit, OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<IncidentSelectTypeComponent>);
  readonly data: IncidentSelectTypeData = inject(MAT_DIALOG_DATA) ?? {};
  readonly locale = inject(L10N_LOCALE);
  private readonly metadataApi = inject(MetadataService);

  readonly EMERGENCY: string = 'emergency';
  readonly OPERATIONS: string = 'operations';
  readonly pageSize: number = 8;

  readonly categoryClassification = signal(this.EMERGENCY);
  readonly metadataViewModel = signal<MetadataViewModel>(null);
  readonly loadErrorMetadata = signal(false);
  readonly loadedMetadata = signal(false);

  readonly emergencyCategories = signal<IncidentCategory[]>(null);
  readonly operationsCategories = signal<IncidentCategory[]>(null);
  readonly selectedCategory = signal<IncidentCategory>(null);
  readonly typesByCategory = signal<{ [id: string]: IncidentType[] }>(null);
  readonly categoryTypes = signal<IncidentType[]>(null);
  readonly selectedEvents = signal<IncidentEvent[]>([]);

  readonly totalCategories = signal(0);
  readonly pageOfCategories = signal<IncidentCategory[]>(null);
  readonly pageOfTypes = signal<IncidentType[]>(null);
  readonly currentCategoryPage = signal<number>(null);
  readonly currentTypePage = signal<number>(null);

  readonly hasCategories = computed(() => !!this.pageOfCategories()?.length);
  readonly hasTypes = computed(() => !!this.pageOfTypes()?.length);

  private readonly disposables = new Subscription();

  ngOnInit() {
    this.fetchMetadata();
  }

  ngOnDestroy() {
    this.disposables.unsubscribe();
  }

  resetComponent() {
    this.categoryClassification.set(this.EMERGENCY);
    this.loadErrorMetadata.set(false);
    this.loadedMetadata.set(false);
    this.currentCategoryPage.set(null);
    this.currentTypePage.set(null);
    this.selectedCategory.set(null);
    this.pageOfTypes.set(null);
    this.categoryTypes.set(null);
    this.loadSelectedEvents();
  }

  fetchMetadata() {
    this.loadErrorMetadata.set(false);
    this.disposables.add(
      this.metadataApi.getMetadata().subscribe({
        next: r => {
          this.metadataViewModel.set(r);
          this.organizeEvents();
          this.loadCategoryPage(1);
        },
        error: _ => {
          this.loadErrorMetadata.set(true);
        }
      })
    );
  }

  organizeEvents() {
    const newEmergencyCategories: IncidentCategory[] = [];
    const newOperationsCategories: IncidentCategory[] = [];
    const newTypesByCategory: { [id: string]: IncidentType[] } = {};

    const metadata = this.metadataViewModel();

    // organize categories by classification
    if (metadata?.categories) {
      metadata.categories.forEach(c => {
        const incidentCategory: IncidentCategory = c;
        const classification = c.classification?.name ? c.classification.name.toLowerCase() : null;
        if (classification === this.OPERATIONS) {
          newOperationsCategories.push(incidentCategory);
        } else {
          newEmergencyCategories.push(incidentCategory);
        }
      });
    }

    // organize types by categoryId
    if (metadata?.types) {
      metadata.types.forEach(t => {
        const categoryId = t.categoryId;
        const incidentType: IncidentType = IncidentModelHelper.convertIncidentType(t);
        const types = newTypesByCategory[categoryId] || [];
        types.push(incidentType);
        newTypesByCategory[categoryId] = types;
      });
    }

    this.emergencyCategories.set(newEmergencyCategories);
    this.operationsCategories.set(newOperationsCategories);
    this.typesByCategory.set(newTypesByCategory);
    this.categoryTypes.set(null);

    this.loadSelectedEvents();
  }

  loadCategoryPage(page: number) {
    const items = this.categoryClassification() == this.EMERGENCY ? this.emergencyCategories() : this.operationsCategories();
    if (!items) return;

    const nextPage: IncidentCategory[] = [];
    let endIndex = this.pageSize * page;
    const startIndex = endIndex - this.pageSize;
    if (startIndex < items.length) {
      if (endIndex > items.length) endIndex = items.length;
      for (let i = startIndex; i < endIndex; i++) {
        nextPage.push(items[i]);
      }
    }

    this.totalCategories.set(items.length);
    this.pageOfCategories.set(nextPage);
    this.currentCategoryPage.set(page);
    this.loadedMetadata.set(true);
  }

  loadTypePage(page: number) {
    const items = this.categoryTypes();
    if (!items) return;

    const nextPage: IncidentType[] = [];
    let endIndex = this.pageSize * page;
    const startIndex = endIndex - this.pageSize;
    if (startIndex < items.length) {
      if (endIndex > items.length) endIndex = items.length;
      for (let i = startIndex; i < endIndex; i++) {
        nextPage.push(items[i]);
      }
    }

    this.pageOfTypes.set(nextPage);
    this.currentTypePage.set(page);
    this.loadedMetadata.set(true);
  }

  loadSelectedEvents() {
    const preselected = this.data.selected || [];
    const metadata = this.metadataViewModel();
    const events: IncidentEvent[] = [];

    if (metadata) {
      preselected.forEach(t => {
        const typeSummary = metadata.types.find(s => s.id === t.id);
        const categorySummary = metadata.categories.find(s => s.id === typeSummary.categoryId);
        const incident: IncidentEvent = {
          incidentCategory: { id: typeSummary.categoryId, name: categorySummary?.name },
          incidentType: t,
        }
        events.push(incident);
      });
    }

    this.selectedEvents.set(events);
  }

  isTypeSelected(incidentType: IncidentType) {
    return this.selectedEvents().some(e => incidentType?.id === e.incidentType?.id);
  }

  onCategoryPageChange(page: number) {
    this.loadCategoryPage(page);
  }

  onTypePageChange(page: number) {
    this.loadTypePage(page);
  }

  onCategoryClassificationChange(newClassification: string) {
    this.categoryClassification.set(newClassification);
    this.selectedCategory.set(null);
    this.pageOfTypes.set(null);
    this.currentTypePage.set(null);
    this.categoryTypes.set(null);

    if (!this.data.selectMultiple) {
      this.loadSelectedEvents();
    }

    if (this.metadataViewModel()) {
      this.loadCategoryPage(1);
    }
  }

  onCategorySelect(newCategory: IncidentCategory) {
    this.selectedCategory.set(newCategory);
    this.pageOfTypes.set(null);
    this.currentTypePage.set(null);
    this.categoryTypes.set(null);

    if (!this.data.selectMultiple) {
      this.loadSelectedEvents();
    }

    const types = this.typesByCategory();
    if (types) {
      this.categoryTypes.set(types[newCategory.id]);
      this.loadTypePage(1);
    }
  }

  onTypeSelect(newType: IncidentType) {
    if (!this.isTypeSelected(newType)) {
      const incident: IncidentEvent = {
        incidentCategory: this.selectedCategory(),
        incidentType: newType,
      }

      this.selectedEvents.update(events => [...events, incident]);
      if (!this.data.selectMultiple) {
        this.onDoneSelecting();
      }
    }
  }

  onClearType(clearedType: IncidentType) {
    this.selectedEvents.update(events => events?.filter(e => e.incidentType?.id !== clearedType?.id));
  }

  onDoneSelecting() {
    const data: IncidentSelectTypeData = { selected: this.selectedEvents() }
    this.dialogRef.close(data);
  }

  onCancelClick() {
    this.dialogRef.close();
  }
}
